import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { SNACK_TIMEOUT, SNACKBAR_NOTIFICATION } from '@app/shared';
import { fromEvent, merge, Observable, Subject, timer } from 'rxjs';
import { mapTo, startWith, switchMap, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  private readonly userActivityEvents$: Observable<any>;
  private readonly inactivityTimeout = 5400000; // 90 minutes
  private stopTimer$ = new Subject<void>();

  constructor(
    private readonly router: Router, 
    private readonly snackBar: MatSnackBar,
    private readonly ngZone: NgZone
  ) {
    this.userActivityEvents$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'click'),
      fromEvent(document, 'scroll')
    );
  }

  startWatching(): void {
    this.stopWatching(); // Ensure any existing timer is stopped before starting a new one

    this.stopTimer$ = new Subject<void>(); // Reinitialize stopTimer$ to allow re-subscribing

    this.ngZone.runOutsideAngular(() => {
      this.userActivityEvents$
        .pipe(
          mapTo('user active'),
          startWith('user active'), // Emit an initial activity event
          switchMap(() => timer(this.inactivityTimeout).pipe(mapTo('user inactive'))),
          takeUntil(this.stopTimer$) // Stop watching after logout
        )
        .subscribe((status) => {
          if (status === 'user inactive') {
            this.logout();
          }
        });
    });
  }

  stopWatching(): void {
    this.stopTimer$.next(); // Stop the idle timer
    this.stopTimer$.complete(); // Complete the observable to clean up
  }

  private logout(): void {
    localStorage.clear();

    this.ngZone.run(() => { // Ensure navigation runs inside Angular zone
      this.stopWatching();
      this.router.navigate(['auth/signin']).then(() => {
        this.snackBar.open(SNACKBAR_NOTIFICATION.SIGNOUT_INACTIVITY, '', { duration: SNACK_TIMEOUT });
      });
    });
  }

}